/**
 * Saved Payment Methods Component
 * Displays and manages saved payment methods (cards, bank accounts)
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { CreditCard, Building2, Trash2, Check } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';

interface SavedPaymentMethod {
  id: number;
  method_type: 'card' | 'bank_account';
  display_name: string;
  token: string; // Card token from Coinflow (customer.cards[].token)
  is_default: boolean;
  method_details: {
    last4?: string;
    brand?: string;
    type?: string; // Card type (VISA, MASTERCARD, etc.)
    expMonth?: number;
    expYear?: number;
    bankName?: string;
    accountType?: string;
  };
}

interface SavedPaymentMethodsProps {
  onSelect?: (method: SavedPaymentMethod) => void;
  showSelectButton?: boolean;
  refreshKey?: number; // Key to trigger refresh
  showHeading?: boolean; // Whether to show the "Saved Payment Methods" heading
}

export const SavedPaymentMethods: React.FC<SavedPaymentMethodsProps> = ({
  onSelect,
  showSelectButton = false,
  refreshKey = 0,
  showHeading = true, // Default to true for backward compatibility
}) => {
  const [methods, setMethods] = useState<SavedPaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState<number | null>(null);

  useEffect(() => {
    fetchMethods();
  }, [refreshKey]); // Refresh when refreshKey changes

  const fetchMethods = async () => {
    try {
      const response = await apiClient.get('/payments/methods');
      setMethods(response || []);
    } catch {
      toast.error('Failed to load saved payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (method: SavedPaymentMethod) => {
    if (!confirm('Are you sure you want to delete this payment method?')) {
      return;
    }

    try {
      // Pass the card token in the request body for Coinflow deletion
      // Since cards are returned directly from Coinflow (not stored in DB),
      // we need to pass the token to delete it
      await apiClient.delete(`/payments/methods/${method.id}`, {
        token: method.token, // Card token from Coinflow customer.cards[].token
      });
      toast.success('Payment method deleted');
      fetchMethods();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete payment method');
    }
  };

  const handleSelect = (method: SavedPaymentMethod) => {
    setSelectedMethod(method.id);
    if (onSelect) {
      onSelect(method);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2 lg:space-y-3">
        <Skeleton className="h-16 lg:h-20 w-full" />
        <Skeleton className="h-16 lg:h-20 w-full" />
      </div>
    );
  }

  if (methods.length === 0) {
    return (
      <div className="text-center py-4 lg:py-8 text-muted-foreground">
        <p className="text-[10px] lg:text-sm font-medium">No saved payment methods</p>
        <p className="text-[9px] lg:text-sm mt-1 lg:mt-2">Save a payment method during checkout to use it here</p>
      </div>
    );
  }

  // Helper function to get card brand icon (inline SVGs for reliability)
  const getCardBrandIcon = (brand?: string) => {
    const brandLower = (brand || '').toLowerCase();
    if (brandLower.includes('visa')) {
      return (
        <svg viewBox="0 0 48 48" className="h-6 w-6 lg:h-7 lg:w-7">
          <rect fill="#1565C0" x="4" y="12" width="40" height="24" rx="3"/>
          <path fill="#FFF" d="M20.5,18l-3,12h-3l3-12H20.5z M32.5,24.8c0,3.2-2.8,5.4-7.1,5.4c-1.8,0-3.6-0.4-4.6-0.8l0.6-2.8 c1.1,0.5,2.5,0.8,4.1,0.8c1.7,0,2.8-0.7,2.8-1.8c0-0.8-0.6-1.3-2.4-2.1c-2.6-1.2-3.8-2.7-3.8-4.6c0-3,2.7-5,6.7-5 c1.5,0,2.9,0.3,3.7,0.6l-0.6,2.7c-0.7-0.3-1.8-0.6-3.1-0.6c-1.6,0-2.5,0.7-2.5,1.6c0,0.7,0.7,1.2,2.5,2 C31.1,20.8,32.5,22.4,32.5,24.8z"/>
        </svg>
      );
    }
    if (brandLower.includes('mastercard') || brandLower.includes('mc')) {
      return (
        <svg viewBox="0 0 48 48" className="h-6 w-6 lg:h-7 lg:w-7">
          <circle fill="#FF5F00" cx="24" cy="24" r="16"/>
          <circle fill="#EB001B" cx="16" cy="24" r="12"/>
          <circle fill="#F79E1B" cx="32" cy="24" r="12"/>
        </svg>
      );
    }
    if (brandLower.includes('amex') || brandLower.includes('american')) {
      return (
        <svg viewBox="0 0 48 48" className="h-6 w-6 lg:h-7 lg:w-7">
          <rect fill="#006FCF" x="4" y="12" width="40" height="24" rx="3"/>
          <path fill="#FFF" d="M14,20h4l2,4l2-4h4v8h-3v-5l-2.5,5h-1l-2.5-5v5h-3V20z M28,20h7l1,2l1-2h3v8h-2v-5l-2,5h-2l-2-5v5h-4V20z"/>
        </svg>
      );
    }
    if (brandLower.includes('discover')) {
      return (
        <svg viewBox="0 0 48 48" className="h-6 w-6 lg:h-7 lg:w-7">
          <rect fill="#F76F00" x="4" y="12" width="40" height="24" rx="3"/>
          <circle fill="#FFF" cx="30" cy="24" r="8"/>
          <path fill="#F76F00" d="M30,18c-3.3,0-6,2.7-6,6s2.7,6,6,6s6-2.7,6-6S33.3,18,30,18z"/>
        </svg>
      );
    }
    return <CreditCard className="h-5 w-5 lg:h-6 lg:w-6 text-white/60" />;
  };

  return (
    <div className="space-y-2.5 lg:space-y-3">
      {showHeading && (
        <h3 className="text-[10px] lg:text-sm font-medium text-white/60 uppercase tracking-wider">Saved Cards</h3>
      )}
      {methods.map((method) => (
        <button
          key={method.id}
          className={`w-full flex items-center gap-3 lg:gap-4 p-3.5 lg:p-4 rounded-2xl transition-all duration-300 backdrop-blur-md
            bg-white/[0.03] hover:bg-white/[0.08]
            border border-white/[0.08] hover:border-white/20
            ${selectedMethod === method.id ? 'border-primary/60 bg-primary/10 ring-1 ring-primary/30 shadow-lg shadow-primary/10' : ''}`}
          onClick={() => showSelectButton && handleSelect(method)}
        >
          {/* Icon Container */}
          <div className="flex-shrink-0 w-11 h-11 lg:w-14 lg:h-14 rounded-xl bg-white/[0.05] flex items-center justify-center">
            {method.method_type === 'card' ? (
              getCardBrandIcon(method.method_details.brand || method.method_details.type)
            ) : (
              <Building2 className="h-5 w-5 lg:h-6 lg:w-6 text-emerald-400" />
            )}
          </div>

          {/* Method Details */}
          <div className="flex-1 text-left min-w-0">
            <div className="flex items-center gap-1.5 lg:gap-2">
              <h4 className="font-semibold text-sm lg:text-base text-white truncate">{method.display_name}</h4>
              {method.is_default && (
                <span className="text-[9px] lg:text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                  Default
                </span>
              )}
            </div>
            {method.method_type === 'card' && (
              <p className="text-[11px] lg:text-xs text-white/50 truncate">
                •••• {method.method_details.last4}
                {method.method_details.expMonth &&
                  method.method_details.expYear &&
                  ` • ${method.method_details.expMonth}/${method.method_details.expYear}`}
              </p>
            )}
            {method.method_type === 'bank_account' && (
              <p className="text-[11px] lg:text-xs text-white/50 truncate">
                {method.method_details.bankName || 'Bank'} •••• {method.method_details.last4}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 lg:gap-2.5 flex-shrink-0">
            {/* Selection Indicator */}
            {showSelectButton && (
              <div className={`w-6 h-6 lg:w-7 lg:h-7 rounded-full border-2 flex items-center justify-center transition-all duration-300
                ${selectedMethod === method.id ? 'border-primary bg-primary shadow-lg shadow-primary/30' : 'border-white/20 bg-white/[0.03]'}`}>
                {selectedMethod === method.id && (
                  <Check className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-white" />
                )}
              </div>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(method);
              }}
              className="h-7 lg:h-8 w-7 lg:w-8 p-0 text-red-400/40 hover:text-red-400 hover:bg-red-500/20 rounded-full transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
            </Button>
          </div>
        </button>
      ))}
    </div>
  );
};

